import { useSuspenseQuery } from "@apollo/client";
import {
  Button,
  CurrencyAmountUI,
  DataTable,
  PrimaryFullScreenSpinner,
} from "@repo/ui";
import { isCurrency, NotFoundError } from "@repo/utils";
import {
  createFileRoute,
  Link,
  Outlet,
  useParams,
} from "@tanstack/react-router";
import { Suspense } from "react";
import { RiAddLine } from "react-icons/ri";
import { UnitsWithPriceUI } from "~/components/Amount/UnitsWithPriceUI";
import { EntityLink } from "~/components/link/EntityLink/EntityLink";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/ledger/transactions_/$transactionId/entries",
)({
  head: () => opsHead("Ledger Transaction Entries"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { transactionId } = useParams({ strict: false });
  if (!transactionId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminLedgerTransactionEntries($transactionId: ID!) {
        ledgerTransaction(id: $transactionId) {
          id
          ledgerEntries {
            id
            direction
            unitAmount {
              amount
              unit
            }
            ledgerAccount {
              id
              name
            }
            unitPrice {
              targetUnit
              price
            }
          }
        }
      }
    `),
    {
      variables: { transactionId },
    },
  );

  const ledgerTransaction = data?.ledgerTransaction;
  if (!ledgerTransaction) {
    throw new NotFoundError();
  }

  const ledgerTransactionEntries = (ledgerTransaction.ledgerEntries ?? [])
    .filter((entry) => Boolean(entry.ledgerAccount?.id))
    .map((entry) => ({
      id: entry.id,
      direction: entry.direction,
      amount: entry.unitAmount.amount,
      unit: entry.unitAmount.unit,
      ledgerAccount: {
        id: entry.ledgerAccount!.id,
        name: entry.ledgerAccount?.name ?? "Unknown",
      },
      unitPrice: entry.unitPrice
        ? {
            targetUnit: entry.unitPrice.targetUnit,
            price: entry.unitPrice.price,
          }
        : null,
    }));

  return (
    <div>
      <div className="text-right">
        <Link
          to="/ledger/transactions/$transactionId/entries/new"
          params={{ transactionId }}
        >
          <Button leftIcon={<RiAddLine />} size="sm">
            Add entries
          </Button>
        </Link>
      </div>

      <DataTable
        data={ledgerTransactionEntries}
        columns={[
          {
            accessorKey: "direction",
            header: "Direction",
          },
          {
            accessorKey: "amount",
            header: "Amount",
            cell: ({ row }) => {
              const { amount, unit, unitPrice } = row.original;

              let displayAmount, displayCurrency;
              if (isCurrency(unit)) {
                displayAmount = amount;
                displayCurrency = unit;
              } else if (unitPrice) {
                displayAmount = unitPrice.price.mul(amount);
                displayCurrency = unitPrice.targetUnit;
              } else {
                throw new Error(`unit ${unit} not supported`);
              }

              return (
                <div className="flex flex-col items-start">
                  <CurrencyAmountUI
                    amount={displayAmount}
                    currency={displayCurrency}
                  />

                  {unitPrice && (
                    <div className="text-tertiary text-xs">
                      <UnitsWithPriceUI
                        unitAmount={amount}
                        price={unitPrice.price}
                        currency={unitPrice.targetUnit}
                      />
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            accessorKey: "ledgerAccount",
            header: "Account",
            cell: ({ row }) => (
              <EntityLink
                to={`/ledger/accounts/${row.original.ledgerAccount.id}`}
              >
                {row.original.ledgerAccount.name}
              </EntityLink>
            ),
          },
          {
            id: "ledgerAccountId",
            header: "Account ID",
            cell: ({ row }) => (
              <span className="font-mono text-xs">
                {row.original.ledgerAccount.id}
              </span>
            ),
          },
        ]}
      />

      <Outlet />
    </div>
  );
}
